import {
  SET_CUSTOMER_FLAGS_KEY,
  SET_FEATURE_FLAGS,
} from '@/store/mutation-types/featureFlagMutations'

import { UPDATE_USER } from '@/store/mutation-types/userMutations'

const DEFAULT_ATTRIBUTES = {
  workloadType: 'client',
}

export default {
  getDefaultCustomerIdHash() {
    return '-'
  },
  // this should only be called once in the app lifecycle
  initializeFeatureFlags(store, SplitFactory) {
    const splitApiKey = process.env.VUE_APP_SPLIT_AUTHORIZATION_KEY

    const splitSDK = SplitFactory({
      core: {
        authorizationKey: splitApiKey,
        key: this.getDefaultCustomerIdHash(), // set key as an empty value. When updateClient is called, this will be updated with the customerIdHash
      },
    })

    // create a watcher that tracks changes to the user.
    // when the user changes, update the Split client with the new customerIdHash
    store.subscribe((mutation) => {
      this.subscribeToStore(
        mutation,
        splitSDK,
        splitApiKey,
        store,
        SplitFactory,
      )
    })
  },
  subscribeToStore(mutation, splitSDK, splitApiKey, store, SplitFactory) {
    if (mutation.type === UPDATE_USER) {
      splitSDK.destroy()

      if (mutation.payload === null || mutation.payload.isGuest) {
        // user is a guest or logged out
        // All guest / anonymous accounts should use the same default value for a customer hash id (e.g. “-”)
        splitSDK = SplitFactory({
          core: {
            authorizationKey: splitApiKey,
            key: this.getDefaultCustomerIdHash(), // set key as an empty value. When updateClient is called, this will be updated with the customerIdHash
          },
        })

        this.updateClient(splitSDK, store, this.getDefaultCustomerIdHash())
      } else {
        splitSDK = SplitFactory({
          core: {
            authorizationKey: splitApiKey,
            key: mutation.payload.customerIdHash, // set key as the customerIdHash
          },
        })

        this.updateClient(splitSDK, store, mutation.payload.customerIdHash)
      }
    }
  },
  updateClient(splitSDK, store, customerIdHash) {
    // NOTE: we don't call `.destroy` on the previous client because it will destroy the underlying factory object as well;
    // that means we'll end up with multiple listeners if the user is logging into different profiles, but we just have to live with that for now!
    const splitClient = splitSDK.client(
      customerIdHash || this.getDefaultCustomerIdHash(),
    ) // use the customerIdHash as the traffic key for the Split client

    splitClient.setAttributes({
      ...DEFAULT_ATTRIBUTES,
    })

    this.onUpdate(store, splitClient, customerIdHash)

    splitClient.on(splitClient.Event.SDK_READY, () => {
      this.onUpdate(store, splitClient, customerIdHash)
    })

    splitClient.on(splitClient.Event.SDK_UPDATE, () => {
      this.onUpdate(store, splitClient, customerIdHash)
    })

    splitClient.on(splitClient.Event.SDK_ERROR, (error) => {
      console.error('Split SDK error:', error)
    })
  },
  onUpdate(store, client, customerIdHash) {
    // Update store with timestamp showing most recent update, including customer hash
    const timeStampedCustomerHash = `${
      customerIdHash || this.getDefaultCustomerIdHash()
    }:${Date.now()}`

    store.commit(SET_CUSTOMER_FLAGS_KEY, timeStampedCustomerHash)

    // set the flags
    this.setFlags(store, client)
  },
  getTreatment(store, client, flagName) {
    // wrapper for Split's getTreatment that includes attributes
    return client.getTreatment(flagName, {
      ...DEFAULT_ATTRIBUTES,
      id: store.state.user.user.customerIdHash || '-',
    })
  },
  setFlags(store, client) {
    // update the store with the feature flags in use
    // simplifies below store.commit to be single line (if print width wasn't 80)
    const getFlagValue = (flagName) => {
      return this.getTreatment(store, client, flagName) === 'on'
    }

    store.commit(SET_FEATURE_FLAGS, {
      // when creating new flags, ensure to add them here
      // example:
      // homePageRedesignActive: getFlagValue(
      //   'release-catering-homepage-redesign',
      // ),
    })
  },
}
